import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DealCard from './DealCard';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SUGGESTIONS = [
  'Vitamins under $15',
  'Best deals at Coles',
  'Baby products on sale',
  'Biggest discounts today',
  'Chocolate under $5',
];

export default function AssistantChat({ shoppingList, onAddToList, onTabChange, favourites = [], onToggleFavourite }) {
  const { getToken } = useAuth();
  const [messages, setMessages]   = useState([{
    role: 'assistant',
    text: "G'day! I'm your Cart Deals assistant 🛒\nAsk me to find deals, compare prices, or search by category — I know every current special across all stores.",
    deals: [],
  }]);
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [listening, setListening] = useState(false);
  const [speakOn, setSpeakOn]     = useState(false);
  const bottomRef   = useRef(null);
  const inputRef    = useRef(null);
  const recognRef   = useRef(null);

  const listSet = new Set((shoppingList || []).map(i => i.name + i.store));
  const favSet  = new Set((favourites  || []).map(f => f.name + f.store));

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // ---- Voice input ----
  const SpeechRecog = window.SpeechRecognition || window.webkitSpeechRecognition;
  const hasVoice    = !!SpeechRecog;

  function startListening() {
    if (!SpeechRecog || recognRef.current) return;
    const r = new SpeechRecog();
    r.lang = 'en-AU';
    r.interimResults = false;
    r.maxAlternatives = 1;
    recognRef.current = r;
    r.onstart  = () => {};
    r.onend    = () => { setListening(false); recognRef.current = null; };
    r.onerror  = e => {
      setListening(false);
      recognRef.current = null;
      if (e.error === 'not-allowed') {
        setMessages(prev => [...prev, { role: 'assistant', text: 'Microphone access was denied. Please allow microphone access in your browser settings and try again.' }]);
      } else if (e.error === 'no-speech' || e.error === 'aborted') {
        // silence — expected on iOS when tapping quickly
      } else {
        setMessages(prev => [...prev, { role: 'assistant', text: `Voice input error: ${e.error}. Try typing instead.` }]);
      }
    };
    r.onresult = e => {
      const text = e.results[0][0].transcript;
      setInput(text);
      inputRef.current?.focus();
    };
    try {
      r.start();
      setListening(true);
    } catch (err) {
      setListening(false);
      setMessages(prev => [...prev, { role: 'assistant', text: 'Could not start voice input. Try typing instead.' }]);
    }
  }

  function stopListening() {
    if (recognRef.current) {
      recognRef.current.stop();
      recognRef.current = null;
    }
    setListening(false);
  }

  // ---- Voice output ----
  const speak = useCallback((text) => {
    if (!speakOn) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-AU';
    u.rate = 1.05;
    window.speechSynthesis.speak(u);
  }, [speakOn]);

  // ---- Send message ----
  async function send(text) {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;
    setInput('');

    const history = messages.map(m => ({ role: m.role, content: m.text }));
    setMessages(prev => [...prev, { role: 'user', text: trimmed, deals: [] }]);
    setLoading(true);

    try {
      const token = getToken();
      const res = await fetch(`${API}/assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: trimmed, history, shoppingList: shoppingList || [] }),
      });
      const data = await res.json();
      const msg  = data.message || data.error || 'Sorry, something went wrong.';

      // Auto-add any deals the AI decided to add
      if (data.addToList?.length && onAddToList) {
        data.addToList.forEach(deal => onAddToList(deal));
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: msg,
        deals: data.deals || [],
        added: data.addToList || [],
      }]);
      speak(msg);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: "Couldn't reach the assistant. Is the backend running?",
        deals: [],
      }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="assistant-wrap">
      <div className="assistant-speak-row">
        <button
          className={`assistant-speak-toggle ${speakOn ? 'assistant-speak-toggle--on' : ''}`}
          onClick={() => { window.speechSynthesis.cancel(); setSpeakOn(v => !v); }}
          title={speakOn ? 'Voice on — click to mute' : 'Enable voice responses'}
        >
          {speakOn ? '🔊 Voice on' : '🔇 Voice off'}
        </button>
      </div>

      <div className="assistant-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`amsg amsg--${msg.role}`}>
            {msg.role === 'assistant' && <div className="amsg-avatar">🛒</div>}
            <div className="amsg-body">
              <div className="amsg-bubble">
                {msg.text.split('\n').map((line, j) => <p key={j}>{line}</p>)}
                {msg.added?.length > 0 && (
                  <p className="amsg-added-note">
                    Added {msg.added.length} item{msg.added.length !== 1 ? 's' : ''} to your list
                  </p>
                )}
              </div>
              {msg.deals?.length > 0 && (
                <div className="amsg-deals">
                  {msg.deals.map((deal, j) => (
                    <div key={j} className="amsg-deal-card">
                      <DealCard
                        deal={deal}
                        onAddToList={onAddToList}
                        inList={listSet.has(deal.name + deal.store)}
                        isFavourited={favSet.has(deal.name + deal.store)}
                        onToggleFavourite={onToggleFavourite}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="amsg amsg--assistant">
            <div className="amsg-avatar">🛒</div>
            <div className="amsg-body">
              <div className="amsg-bubble amsg-bubble--typing">
                <span /><span /><span />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Suggestion chips — only show before first user message */}
      {messages.filter(m => m.role === 'user').length === 0 && (
        <div className="assistant-suggestions">
          {SUGGESTIONS.map(s => (
            <button key={s} className="assistant-suggestion" onClick={() => send(s)}>
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="assistant-input-row">
        <input
          ref={inputRef}
          className="assistant-input"
          placeholder={`Ask me anything… e.g. "vitamins under $15"`}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
          disabled={loading}
        />
        {hasVoice && (
          <button
            className={`assistant-mic ${listening ? 'assistant-mic--on' : ''}`}
            onClick={() => listening ? stopListening() : startListening()}
            title={listening ? 'Tap to stop recording' : 'Tap to start recording'}
            aria-label={listening ? 'Stop recording' : 'Start recording'}
          >
            {listening ? '🔴' : '🎤'}
          </button>
        )}
        <button
          className="assistant-send"
          onClick={() => send()}
          disabled={loading || !input.trim()}
        >
          ➤
        </button>
      </div>
    </div>
  );
}
